import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Typography,
  Tag,
  Button,
  Timeline,
  Space,
  message,
  Spin,
  Empty,
  Popconfirm,
  Input,
  Modal,
  Alert,
  Descriptions,
} from 'antd';
import {
  ClockCircleOutlined,
  StopOutlined,
  ReloadOutlined,
  CreditCardOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { lifecycleApi } from '../../services/lifecycle';
import type { LifecycleStatus, LifecycleEvent } from '../../types/lifecycle';
import {
  LifecycleStatusNames,
  LifecycleEventTypeNames,
  getLifecycleStatusColor,
} from '../../types/lifecycle';
import { ROUTES } from '../../config/routes';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

export const AccountLifecycle: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<LifecycleStatus | null>(null);
  const [events, setEvents] = useState<LifecycleEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // 获取生命周期状态
  const fetchStatus = useCallback(async () => {
    try {
      const res = await lifecycleApi.getStatus();
      if (res.data) {
        setStatus(res.data);
      }
    } catch (error) {
      console.error('Failed to fetch lifecycle status:', error);
    }
  }, []);

  // 获取生命周期事件
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await lifecycleApi.getEvents(page, pageSize);
      if (res.data) {
        setEvents(res.data.items || []);
        setTotal(res.data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch lifecycle events:', error);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchStatus();
    fetchEvents();
  }, [fetchStatus, fetchEvents]);

  // 发起注销
  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      message.warning('请输入注销原因');
      return;
    }

    setActionLoading(true);
    try {
      const res = await lifecycleApi.cancelAccount(cancelReason);
      if (res.data) {
        message.success('已发起注销申请，进入冷静期');
        setCancelModalVisible(false);
        setCancelReason('');
        fetchStatus();
        fetchEvents();
      }
    } catch (error) {
      console.error('Failed to cancel account:', error);
    } finally {
      setActionLoading(false);
    }
  };

  // 恢复账户
  const handleReactivate = async () => {
    setActionLoading(true);
    try {
      const res = await lifecycleApi.reactivateAccount();
      if (res.data) {
        message.success('账户已恢复');
        fetchStatus();
        fetchEvents();
      }
    } catch (error) {
      console.error('Failed to reactivate account:', error);
    } finally {
      setActionLoading(false);
    }
  };

  // 获取状态说明文字
  const getStatusDescription = (currentStatus: string) => {
    const descriptions: Record<string, string> = {
      trial: '您正在试用期，可使用所有功能。试用结束后账户将冻结。',
      active: '您的账户状态正常，可正常使用所有功能。',
      grace_period: '您的订阅已过期，处于宽限期内。请尽快续费以避免账户冻结。',
      frozen: '您的账户已被冻结，仅可导出数据或续费恢复。',
      frozen_trial: '您的试用期已结束，账户已被冻结。请订阅以恢复使用。',
      cancelling: '您已申请注销账户，正在冷静期内。冷静期结束后将清除所有数据。',
    };
    return descriptions[currentStatus] || '';
  };

  // 计算冷静期剩余时间
  const getCooldownRemaining = (cooldownEnds: string | null) => {
    if (!cooldownEnds) return null;
    const ends = new Date(cooldownEnds);
    const now = new Date();
    const diffTime = ends.getTime() - now.getTime();
    if (diffTime <= 0) return '已结束';
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} 天`;
  };

  // 渲染操作区
  const renderActionArea = () => {
    if (!status) return null;

    const currentStatus = status.current_status;

    // active/trial 状态：显示注销按钮
    if (currentStatus === 'active' || currentStatus === 'trial') {
      return (
        <Card title="账户操作" style={{ marginBottom: 24 }}>
          <Alert
            message="注销账户"
            description="注销账户将进入冷静期，冷静期结束后将清除所有数据且不可恢复。"
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Button danger icon={<StopOutlined />} onClick={() => setCancelModalVisible(true)}>
            注销账户
          </Button>
        </Card>
      );
    }

    // cancelling 状态：显示取消注销按钮
    if (currentStatus === 'cancelling') {
      const cooldownRemaining = getCooldownRemaining(status.cancellation_cooldown_ends);
      return (
        <Card title="账户操作" style={{ marginBottom: 24 }}>
          <Alert
            message="账户注销中"
            description={
              <span>
                您的账户正在注销流程中。
                {cooldownRemaining && (
                  <Text strong> 冷静期剩余：{cooldownRemaining}</Text>
                )}
              </span>
            }
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Popconfirm
            title="确定取消注销并恢复账户？"
            onConfirm={handleReactivate}
            okText="确定"
            cancelText="取消"
          >
            <Button type="primary" icon={<ReloadOutlined />} loading={actionLoading}>
              取消注销，恢复账户
            </Button>
          </Popconfirm>
        </Card>
      );
    }

    // frozen/frozen_trial 状态：显示续费提示
    if (currentStatus === 'frozen' || currentStatus === 'frozen_trial') {
      return (
        <Card title="账户操作" style={{ marginBottom: 24 }}>
          <Alert
            message="账户已冻结"
            description="您的账户已被冻结，仅可导出数据或续费恢复。"
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Space>
            <Button
              type="primary"
              icon={<CreditCardOutlined />}
              onClick={() => navigate(ROUTES.SETTINGS_SUBSCRIPTION)}
            >
              立即续费
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => navigate(ROUTES.SETTINGS_DATA_EXPORT)}
            >
              导出数据
            </Button>
          </Space>
        </Card>
      );
    }

    // grace_period 状态：显示续费提示
    if (currentStatus === 'grace_period') {
      return (
        <Card title="账户操作" style={{ marginBottom: 24 }}>
          <Alert
            message="订阅已过期"
            description="您的订阅已过期，处于宽限期内。请尽快续费以避免账户冻结。"
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Button
            type="primary"
            icon={<CreditCardOutlined />}
            onClick={() => navigate(ROUTES.SETTINGS_SUBSCRIPTION)}
          >
            立即续费
          </Button>
        </Card>
      );
    }

    return null;
  };

  return (
    <Spin spinning={loading}>
      <div>
        <Title level={4} style={{ marginBottom: 24 }}>
          账户管理
        </Title>

        {/* 账户状态卡片 */}
        {status && (
          <Card style={{ marginBottom: 24 }}>
            <Descriptions column={2}>
              <Descriptions.Item label="当前状态">
                <Tag
                  color={getLifecycleStatusColor(status.current_status)}
                  style={{ fontSize: 14, padding: '4px 12px' }}
                >
                  {LifecycleStatusNames[status.current_status] || status.current_status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="状态持续">
                {status.days_in_status} 天
              </Descriptions.Item>
              <Descriptions.Item label="状态说明" span={2}>
                {getStatusDescription(status.current_status)}
              </Descriptions.Item>
              {status.next_action && status.next_action_date && (
                <Descriptions.Item label="下一步操作" span={2}>
                  <Text type="warning">
                    将于 {new Date(status.next_action_date).toLocaleDateString()} {status.next_action === 'freeze' ? '自动冻结' : status.next_action === 'clean' ? '清理数据' : status.next_action}
                  </Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        )}

        {/* 操作区 */}
        {renderActionArea()}

        {/* 生命周期事件 Timeline */}
        <Card title="账户事件记录">
          {events.length === 0 ? (
            <Empty description="暂无事件记录" />
          ) : (
            <>
              <Timeline
                mode="left"
                items={events.map((event) => ({
                  color: event.event_type === 'frozen' || event.event_type === 'cancelled' || event.event_type === 'cleaned' ? 'red' : 'blue',
                  children: (
                    <div>
                      <div style={{ marginBottom: 4 }}>
                        <Text strong>{LifecycleEventTypeNames[event.event_type] || event.event_type}</Text>
                        <Text type="secondary" style={{ marginLeft: 12 }}>
                          {new Date(event.created_at).toLocaleString()}
                        </Text>
                      </div>
                      <div style={{ marginBottom: 4 }}>
                        <Text type="secondary">
                          状态变更：{LifecycleStatusNames[event.from_status] || event.from_status} → {LifecycleStatusNames[event.to_status] || event.to_status}
                        </Text>
                      </div>
                      {event.reason && (
                        <div>
                          <Text type="secondary">原因：{event.reason}</Text>
                        </div>
                      )}
                    </div>
                  ),
                }))}
              />
              {total > pageSize && (
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                  <Button onClick={() => setPage(page + 1)} disabled={events.length >= total}>
                    加载更多
                  </Button>
                </div>
              )}
            </>
          )}
        </Card>

        {/* 注销确认弹窗 */}
        <Modal
          title="确认注销账户"
          open={cancelModalVisible}
          onCancel={() => {
            setCancelModalVisible(false);
            setCancelReason('');
          }}
          onOk={handleCancel}
          confirmLoading={actionLoading}
          okText="确认注销"
          okButtonProps={{ danger: true }}
        >
          <Alert
            message="警告：注销账户将清除所有数据"
            description="账户注销后将进入冷静期，冷静期结束后将永久清除所有数据，此操作不可撤销。"
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Paragraph>请输入注销原因：</Paragraph>
          <TextArea
            rows={4}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="请说明注销原因..."
            maxLength={500}
            showCount
          />
        </Modal>
      </div>
    </Spin>
  );
};

export default AccountLifecycle;
